import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useApi } from '../hooks/useApi';
import { useToast } from '../contexts/ToastContext';
import { getStatusClasses } from '../utils/statusUtils';
import LoadingSpinner from './LoadingSpinner';
import ErrorBoundary from './ErrorBoundary';

/**
 * KnowledgeBaseSyncStatusPanel Component
 * 
 * Displays detailed Knowledge Base sync status for a specific parrot with:
 * - Overall sync health indicator
 * - Individual KB item status breakdown
 * - Manual retry controls for failed operations
 * - Estimated completion times for ongoing operations
 * - Expandable details for each KB source
 * 
 * Requirements: 3.1, 3.2, 3.4, 3.6
 */
const KnowledgeBaseSyncStatusPanel = ({
    parrotId,
    parrotName,
    className='',
    showHeader = true,
    autoRefresh = true,
    refreshInterval = 30000,
    onStatusChange = null,
    expandedByDefault = false
}) => {
    const { theme } = useTheme();
    const { execute, loading } = useApi();
    const { showToast } = useToast();

    // State management
    const [syncStatus, setSyncStatus] = useState(null);
    const [expanded, setExpanded] = useState(expandedByDefault);
    const [retryingOperations, setRetryingOperations] = useState(new Set());
    const [lastUpdated, setLastUpdated] = useState(null);
    const [error, setError] = useState(null);

    // Fetch sync status for the parrot
    const fetchSyncStatus = useCallback(async () => {
        if (!parrotId) return;

        try {
            setError(null);
            const status = await execute(async (api) => {
                return await api.get(`/api/parrots/${parrotId}/knowledge-base-status`);
            }, { showErrorToast: false });

            setSyncStatus(status);
            setLastUpdated(new Date());

            // Notify parent of status change
            if (onStatusChange) {
                onStatusChange(status);
            }
        } catch (error) {
            console.error(`Failed to fetch sync status for parrot ${parrotId}:`, error);
            setError(error.message);
            setSyncStatus(null);
        }
    }, [parrotId, execute, onStatusChange]);

    // Retry failed sync operation
    const retrySync = useCallback(async (sourceId, sourceType) => {
        const operationKey = `${sourceId}-${sourceType}`;
        setRetryingOperations(prev => new Set([...prev, operationKey]));

        try {
            await execute(async (api) => {
                return await api.post(`/api/knowledge-base/${sourceId}/retry-sync`, {
                    parrot_id: parrotId
                });
            });

            showToast('Sync retry initiated successfully', 'success');

            // Refresh status after a short delay
            setTimeout(() => {
                fetchSyncStatus();
            }, 2000);
        } catch (error) {
            console.error('Failed to retry sync operation:', error);
            showToast(error.message || 'Failed to retry sync operation', 'error');
        } finally {
            setRetryingOperations(prev => {
                const newSet = new Set(prev);
                newSet.delete(operationKey);
                return newSet;
            });
        }
    }, [parrotId, execute, showToast, fetchSyncStatus]);

    // Initial fetch and periodic refresh
    useEffect(() => {
        fetchSyncStatus();

        if (autoRefresh) {
            const interval = setInterval(fetchSyncStatus, refreshInterval);
            return () => clearInterval(interval);
        }
    }, [fetchSyncStatus, autoRefresh, refreshInterval]);

    // Get status badge classes
    const getStatusBadgeClasses = useCallback((status) => {
        const baseClasses = 'px-2 py-1 rounded-full text-xs font-medium border';

        switch (status) {
            case 'completed':
            case 'ready':
                return `${baseClasses} ${getStatusClasses(theme, 'success')}`;
            case 'processing':
            case 'queued':
                return `${baseClasses} ${getStatusClasses(theme, 'info')}`;
            case 'failed':
            case 'error':
                return `${baseClasses} ${getStatusClasses(theme, 'error')}`;
            default:
                return `${baseClasses} ${getStatusClasses(theme, 'secondary')}`;
        }
    }, [theme]);

    // Get overall status indicator
    const getOverallStatusIndicator = useCallback(() => {
        if (!syncStatus) return { status: 'unknown', color: 'gray', icon: '?' };

        const { aggregate_status } = syncStatus;
        
        if (aggregate_status.has_failures) {
            return { 
                status: 'error', 
                color: 'red', 
                icon: '⚠️',
                text: `${aggregate_status.failed_sources} failed`
            };
        }
        
        if (aggregate_status.in_progress) {
            return { 
                status: 'processing', 
                color: 'blue', 
                icon: '⏳',
                text: `${aggregate_status.pending_sources} processing`
            };
        }
        
        if (aggregate_status.all_completed && aggregate_status.total_sources > 0) {
            return { 
                status: 'ready', 
                color: 'green', 
                icon: '✅',
                text: 'All ready'
            };
        }
        
        if (aggregate_status.total_sources === 0) {
            return { 
                status: 'empty', 
                color: 'gray', 
                icon: '📄',
                text: 'No sources'
            };
        }

        return { 
            status: 'partial', 
            color: 'yellow', 
            icon: '⚡',
            text: `${aggregate_status.ready_sources} of ${aggregate_status.total_sources} ready`
        };
    }, [syncStatus]);

    // Format estimated completion time
    const formatEstimatedCompletion = useCallback((timestamp) => {
        if (!timestamp) return null;

        const now = new Date();
        const completion = new Date(timestamp);
        const diffMs = completion.getTime() - now.getTime();

        if (diffMs <= 0) return 'Completing soon';

        const diffMinutes = Math.ceil(diffMs / (1000 * 60));

        if (diffMinutes < 60) {
            return `~${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''}`;
        }

        const diffHours = Math.ceil(diffMinutes / 60);
        return `~${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
    }, []);

    // Render source item
    const renderSourceItem = useCallback((source) => {
        const operationKey = `${source.source_id}-${source.source_type}`;
        const isRetrying = retryingOperations.has(operationKey);

        return (
            <div key={`${source.source_id}-${source.source_type}`} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg dark:bg-gray-900">
                <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {source.source_name || source.source_id}
                            </span>
                            <span className={getStatusBadgeClasses(source.status)}>
                                {source.status}
                            </span>
                        </div>

                        <div className="mt-1 flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                            <span>Type: {source.source_type}</span>
                            {source.source_info?.file_type && (
                                <span>Format: {source.source_info.file_type.toUpperCase()}</span>
                            )}
                            {source.source_info?.file_size && (
                                <span>Size: {(source.source_info.file_size / 1024 / 1024).toFixed(1)}MB</span>
                            )}
                        </div>

                        {source.error_message && (
                            <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
                                <p className="text-xs text-red-800 dark:text-red-200">
                                    {source.error_message}
                                </p>
                            </div>
                        )}

                        {source.last_sync_attempt && (
                            <div className="mt-1">
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                    Last attempt: {new Date(source.last_sync_attempt).toLocaleString()}
                                </span>
                            </div>
                        )}

                        {source.retry_count > 0 && (
                            <div className="mt-1">
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                    Retry attempts: {source.retry_count}
                                </span>
                            </div>
                        )}
                    </div>

                    {source.status === 'failed' && (
                        <button
                            onClick={() => retrySync(source.source_id, source.source_type)}
                            disabled={isRetrying}
                            className="ml-3 px-3 py-1 text-xs font-medium text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 border border-red-300 dark:border-red-600 rounded hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {isRetrying ? (
                                <div className="flex items-center space-x-1">
                                    <LoadingSpinner size="sm" color="red" />
                                    <span>Retrying...</span>
                                </div>
                            ) : (
                                'Retry'
                            )}
                        </button>
                    )}
                </div>
            </div>
        );
    }, [getStatusBadgeClasses, retryingOperations, retrySync]);

    // Loading state
    if (loading && !syncStatus) {
        return (
            <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 ${className}`}>
                <div className="flex items-center justify-center py-4">
                    <LoadingSpinner size="sm" />
                    <span className="ml-2 text-sm text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">
                        Loading sync status...
                    </span>
                </div>
            </div>
        );
    }

    // Error state
    if (error && !syncStatus) {
        return (
            <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 ${className}`}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center">
                        <span className="text-red-500 mr-2">⚠️</span>
                        <span className="text-sm text-red-600 dark:text-red-400">
                            Failed to load sync status
                        </span>
                    </div>
                    <button
                        onClick={fetchSyncStatus}
                        className="text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 underline"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    // No sync status available
    if (!syncStatus) {
        return (
            <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 ${className}`}>
                <div className="text-center py-4">
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                        No sync status available
                    </span>
                </div>
            </div>
        );
    }

    const overallStatus = getOverallStatusIndicator();
    const { knowledge_base_sources = [], aggregate_status } = syncStatus;

    return (
        <ErrorBoundary>
            <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 ${className}`}>
                {/* Header */}
                {showHeader && (
                    <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                                Knowledge Base Status
                            </h3>
                            {lastUpdated && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                    Updated {lastUpdated.toLocaleTimeString()}
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {/* Overall Status Summary */}
                <div className="p-4">
                    <div
                        className="flex items-center justify-between cursor-pointer"
                        onClick={() => setExpanded(!expanded)}
                    >
                        <div className="flex items-center space-x-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${
                                overallStatus.color === 'green' ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' :
                                overallStatus.color === 'blue' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' :
                                overallStatus.color === 'red' ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' :
                                overallStatus.color === 'yellow' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400' :
                                'bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400'
                            }`}>
                                {overallStatus.icon}
                            </div>

                            <div>
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                    {parrotName} Knowledge Base
                                </div>
                                <div className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">
                                    {overallStatus.text}
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center space-x-2">
                            {aggregate_status.in_progress && (
                                <div className="flex items-center space-x-1">
                                    <LoadingSpinner size="sm" color="blue" />
                                    <span className="text-xs text-blue-600 dark:text-blue-400">
                                        Processing...
                                    </span>
                                </div>
                            )}

                            <svg
                                className={`w-5 h-5 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </div>
                    </div>

                    {/* Quick Stats */}
                    {aggregate_status.total_sources > 0 && (
                        <div className="mt-3 grid grid-cols-3 gap-4 text-center">
                            <div>
                                <div className="text-lg font-semibold text-green-600 dark:text-green-400">
                                    {aggregate_status.ready_sources}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">Ready</div>
                            </div>
                            <div>
                                <div className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                                    {aggregate_status.pending_sources}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">Processing</div>
                            </div>
                            <div>
                                <div className="text-lg font-semibold text-red-600 dark:text-red-400">
                                    {aggregate_status.failed_sources}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">Failed</div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Detailed Status (Expandable) */}
                {expanded && (
                    <div className="border-t border-gray-200 dark:border-gray-700 p-4">
                        {knowledge_base_sources.length === 0 ? (
                            <div className="text-center py-6">
                                <div className="text-gray-400 dark:text-gray-500 mb-2">
                                    <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                </div>
                                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                                    No Knowledge Base Sources
                                </h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    This parrot doesn't have any knowledge base files or URLs configured yet.
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                <div className="flex items-center justify-between mb-3">
                                    <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                                        Knowledge Base Sources ({knowledge_base_sources.length})
                                    </h4>
                                    {aggregate_status.has_failures && (
                                        <button
                                            onClick={() => {
                                                // Retry all failed operations
                                                knowledge_base_sources
                                                    .filter(source => source.status === 'failed')
                                                    .forEach(source => retrySync(source.source_id, source.source_type));
                                            }}
                                            className="text-xs text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 font-medium"
                                        >
                                            Retry All Failed
                                        </button>
                                    )}
                                </div>

                                {knowledge_base_sources.map(renderSourceItem)}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </ErrorBoundary>
    );
};

export default KnowledgeBaseSyncStatusPanel;
